import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { environment } from '@environments/environment';
import { paths } from '@platform/paths';
import { KeyboardService } from './keyboard.service';
import { NavigationService } from './navigation.service';

declare const cordova;

@Injectable({
  providedIn: 'root',
})
export class MobileAppService {
  private readonly navigationService = inject(NavigationService);
  private readonly keyboardService = inject(KeyboardService);
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);

  private readonly isMobileApp = environment.mobileApp?.isMobileApp ?? false;
  readonly isAndroid = Capacitor.getPlatform() === 'android';
  private readonly APP_SCHEME = 'mymablandlord://';

  applyAppFeatures(): void {
    // applies on web too - to improve user experience
    this.keyboardService.keepActiveInputInViewOnKeyboardOpen();
    if (!this.isMobileApp) return;

    this.handleAndroidBackButton();
    this.handleDeepLinks();
    this.handleStatusBar();
    if (this.isAndroid) this.initializeAndroidSingular();
  }

  handleAndroidBackButton(): void {
    App.addListener('backButton', () => {
      this.navigationService.back();
    });
  }

  handleStatusBar(): void {
    StatusBar.setStyle({ style: Style.Light });
    StatusBar.setBackgroundColor({ color: '#ffffff' });

    if (this.isAndroid) document.body.classList.add('mobile-safe-area-android-padding');
  }

  handleDeepLinks(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        if (this.handleIOSSingularLink(event)) return;
        if (this.handleSchemeUrls(event)) return;
        if (this.handleAndroidDeepLinks(event)) return;
      });
    });
  }

  handleAndroidDeepLinks(event: URLOpenListenerEvent): boolean {
    if (!this.isAndroid) return false;

    const url = new URL(event.url);
    const redirect = `${url.pathname}${url.search}&appDeepLink=true`;
    return this.redirectOnAppDeepLink(redirect);
  }

  redirectOnAppDeepLink(redirect: string): boolean {
    if (!redirect?.includes('auth')) return false;
    this.router.navigateByUrl(redirect);
    return true;
  }

  handleSchemeUrls(event: URLOpenListenerEvent): boolean {
    if (!event.url?.includes(this.APP_SCHEME)) return false;
    const [, redirect] = event.url?.split('://') || [];
    return this.redirectOnAppDeepLink(`${redirect}&appDeepLink=true`);
  }

  singularSignUpAction(passthroughData?: string): void {
    const referralCode = passthroughData || environment.mobileApp?.referralCode;
    this.router.navigate([paths.SIGN_UP, paths.signUp.Landlord], { queryParams: { 'referral-code': referralCode } });
  }

  handleIOSSingularLink(event: URLOpenListenerEvent): boolean {
    const url = new URL(event.url);
    const passthroughData = url.searchParams.get('passthrough');
    const deferred = url?.searchParams.get('deferred') === 'true';
    if (!deferred && !passthroughData) return false;
    this.singularSignUpAction(passthroughData);
    return true;
  }

  initializeAndroidSingular(): void {
    try {
      document.addEventListener('deviceready', () => {
        if (!cordova?.plugins?.SingularCordovaSdk) return;
        const singularConfig = new cordova.plugins.SingularCordovaSdk.SingularConfig(
          environment.mobileApp?.singularConfig.key,
          environment.mobileApp?.singularConfig.secret,
        );
        const singularSignUpAction = (passthrough: string): void => this.singularSignUpAction(passthrough);
        const linkHandler = function (data): void {
          singularSignUpAction(data.passthrough);
        };

        singularConfig.withSingularLink(linkHandler);
        singularConfig.withLoggingEnabled();
        cordova.plugins.SingularCordovaSdk.init(singularConfig);
      });
    } catch (error) {
      console.info('Error init singular', error);
    }
  }
}
